/** @format */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
	CalendarMonthlyTimesheets,
	FilterToggleStates,
	Timesheet,
	TimesheetEntry2,
	TimesheetEntryColors,
	TimesheetStatus,
} from 'src/app/store/application';
import * as HelperFunctions from 'src/assets/helper-functions/helper-functions';

@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CalendarComponent implements OnInit, OnChanges {
	@Input() isYearView!: boolean;
	@Input() calendarData!: CalendarMonthlyTimesheets;
	@Input() filterToggleStates!: FilterToggleStates;
	@Input() isSelectedCalendarInstance = false;
	@Input() selectedTimesheet: Timesheet | null = null;
	@Output() selectedTimesheetEmitter = new EventEmitter<Timesheet>();
	@Output() handleRedirectToTimesheet = new EventEmitter<string>();

	monthName = '';
	firstDayofMonth = 0;
	lastDateofMonth = 0;
	lastDayofMonth = 0;
	lastDateofLastMonth = 0;

	monthlyRelatedEntries: any[] = [];

	calendarHTML = '';
	selectedWeekNumber = -1;

	showPayPeriod = true;
	showPayDay = true;
	showStatHoliday = true;
	showHolidayClosure = true;
	showCDO = true;
	showTimesheets = true;
	showCurrentDay = false;

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	constructor() {}

	ngOnInit(): void {
		this.setFilterOptions();
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.monthName = HelperFunctions.getMonthName(this.calendarData.year, this.calendarData.month);

		if (changes.filterToggleStates) {
			this.setFilterOptions();
		}

		// handle the initial load of the entry view when there is no week selected
		if (this.getIsTimesheetView()) {
			if (this.isSelectedCalendarInstance && this.isSelectedCalendarInstance !== changes.isSelectedCalendarInstance?.previousValue) {
				if (this.selectedTimesheet === null) {
					const date = new Date(new Date().setHours(0, 0, 0, 0));
					let timesheet = !!changes.selectedTimesheet?.currentValue
						? changes.selectedTimesheet.currentValue
						: this.calendarData.timesheets.filter((timesheet) => {
								return HelperFunctions.getIsTimesheetRelated(
									timesheet.startDate,
									timesheet.endDate,
									date.getMonth(),
									date.getFullYear()
								);
						  });

					if (Array.isArray(timesheet)) {
						timesheet = timesheet.find((entry) => HelperFunctions.getDateIsWithinBounds(entry.startDate, entry.endDate, date));
					}

					if (!!timesheet && this.getIsTimesheetView()) {
						// this.setSelectedTimesheet(timesheet);
						this.emitCurrentTimesheet(timesheet);
					}
				}
			}
		}
	}

	setFilterOptions() {
		this.showPayPeriod = this.filterToggleStates.payPeriod;
		this.showPayDay = this.filterToggleStates.payDay;
		this.showStatHoliday = this.filterToggleStates.statHoliday;
		this.showHolidayClosure = this.filterToggleStates.holidayClosure;
		this.showCDO = this.filterToggleStates.cdo;
		this.showTimesheets = this.filterToggleStates.timesheets;
		// this.showCurrentDay = !this.isYearView;
		this.showCurrentDay = true;
	}

	emitCurrentTimesheet(timesheet: Timesheet) {
		this.selectedTimesheetEmitter.emit(timesheet);
	}

	getIsSelectedTimesheet(timesheet: Timesheet) {
		if (this.getIsYearView() || !this.isSelectedCalendarInstance) {
			return false;
		}

		return this.selectedTimesheet && this.selectedTimesheet?.startDate.toString() === timesheet.startDate.toString();
	}

	getIsStatHoliday(timesheet: Timesheet, daysFromStart: number) {
		const entryMetadata = timesheet.metadata[daysFromStart];
		return this.showStatHoliday && entryMetadata.isStat && this.getShouldShowDay(timesheet, daysFromStart);
	}

	getStatHolidayName(timesheet: Timesheet, daysFromStart: number) {
		const statName =
			this.getIsStatHoliday(timesheet, daysFromStart) && timesheet.metadata[daysFromStart].statHoliday
				? `${timesheet.metadata[daysFromStart].statHoliday?.name} (Observed)`
				: '';

		return statName;
	}

	getShouldShowDay(timesheet: Timesheet, daysFromStart: number) {
		if (this.getIsTimesheetView()) {
			return false;
		}

		const currentDate = HelperFunctions.getFutureDate(timesheet.startDate, daysFromStart);
		return currentDate.getFullYear() === this.calendarData.year && currentDate.getMonth() === this.calendarData.month;
	}

	getShouldShadeTimesheetEntries = (timesheet: Timesheet, daysFromPayPeriodStart: number) =>
		this.showPayPeriod && timesheet.payPeriodIndex % 2 === 0 && !this.getIsNeighborMonth(timesheet, daysFromPayPeriodStart);

	getIsTimesheetView = () => !this.isYearView;
	getIsYearView = () => this.isYearView;

	handleTimesheetClick(timesheet: Timesheet) {
		if (this.getIsYearView()) this.loadTimesheet(timesheet);
		else this.emitCurrentTimesheet(timesheet);
	}

	loadTimesheet(timesheet: Timesheet) {
		if (this.getIsTimesheetView()) {
			return;
		}
		const startDate = timesheet.startDate;
		const year = startDate.getFullYear();
		const month = HelperFunctions.convertSingleConstToDoubleDigitStr(startDate.getMonth() + 1);
		const date = HelperFunctions.convertSingleConstToDoubleDigitStr(startDate.getDate());

		const timesheetURLDate = `${year}${month}${date}`;
		const timesheetURL = `/timesheet;timesheetDate=${timesheetURLDate}`;
		this.handleRedirectToTimesheet.emit(timesheetURL);
	}

	// setSelectedTimesheet(timesheet: Timesheet) {
	//   if (this.getIsYearView()) {
	//     return;
	//   }

	//   this.emitCurrentTimesheet(timesheet);
	// }

	getShouldEnableDayHover(timesheet: Timesheet, daysFromStart: number) {
		return (
			!this.getIsNeighborMonth(timesheet, daysFromStart) &&
			(this.getShouldShowBasicTooltip(timesheet, daysFromStart) || this.getShouldShowAdvancedTooltip(timesheet, daysFromStart))
		);
	}

	getShouldShowBasicTooltip(timesheet: Timesheet, daysFromStart: number) {
		return !this.getIsNeighborMonth(timesheet, daysFromStart) && this.isFilterEntry(timesheet, daysFromStart);
	}

	getShouldShowAdvancedTooltip(timesheet: Timesheet, daysFromStart: number) {
		return (
			this.showTimesheets &&
			!this.getIsNeighborMonth(timesheet, daysFromStart) &&
			timesheet.entries.some((entry) => entry.durations[daysFromStart] > 0)
		);
	}

	getIsPayDay(timesheet: Timesheet, daysFromStart: number) {
		const currentDate = HelperFunctions.getFutureDate(timesheet.startDate, daysFromStart);
		const hasMatchingPayday = this.calendarData.payDates.some((payDate) => HelperFunctions.getDifferenceInDays(currentDate, payDate) === 0);
		return this.showPayDay && this.getShouldShowDay(timesheet, daysFromStart) && hasMatchingPayday;
	}

	getIsHolidayClosure(timesheet: Timesheet, daysFromStart: number) {
		if (!this.showHolidayClosure) {
			return false;
		}

		const currentDate = HelperFunctions.getFutureDate(timesheet.startDate, daysFromStart);
		const isStat = timesheet.metadata[daysFromStart].isStat;
		const isWorkingDay = !isStat && timesheet.entries.some((entry) => entry.durations[daysFromStart] > 0);

		return (
			isWorkingDay &&
			this.getShouldShowDay(timesheet, daysFromStart) &&
			currentDate.getMonth() == 11 /*December*/ &&
			currentDate.getDate() > 26 &&
			currentDate.getDate() <= 31
		);
	}

	getIsCDODay(timesheet: Timesheet, daysFromStart: number) {
		const isCDO = timesheet.metadata[daysFromStart].isCDO;
		return this.showCDO && isCDO && !this.getIsNeighborMonth(timesheet, daysFromStart);
	}

	getShouldShowDate = (timesheet: Timesheet, daysFromStart: number) => this.getShouldShowDay(timesheet, daysFromStart) || this.getIsTimesheetView();

	getCurrentDate(timesheet: Timesheet, daysFromStart: number) {
		if (!this.getShouldShowDate(timesheet, daysFromStart)) {
			return '';
		}

		const currentDate = HelperFunctions.getFutureDate(timesheet.startDate, daysFromStart);
		return currentDate.getDate();
	}

	getIsNeighborMonth(timesheet: Timesheet, daysFromStart: number) {
		const currentDate = HelperFunctions.getFutureDate(timesheet.startDate, daysFromStart);
		return this.calendarData.month !== currentDate.getMonth();
	}

	shouldHighlightCurrentDay(timesheet: Timesheet, daysFromStart: number) {
		if (!this.showCurrentDay) {
			return false;
		}

		const currentDate = HelperFunctions.getCurrentDate();
		const entryDate = HelperFunctions.getFutureDate(timesheet.startDate, daysFromStart);

		if (this.calendarData.year !== currentDate.getFullYear()) {
			return false;
		}

		if (this.calendarData.month !== currentDate.getMonth()) {
			return false;
		}

		return currentDate.toString() === entryDate.toString();
	}

	getCalendarBarStyles(timesheet: Timesheet, daysFromStart: number) {
		let linearGradient = '';
		let totalTimePerDay = 0;
		let percentStart = 0;
		let percentUsed = 0;
		let percentRemaining = 100;
		const entriesCurrentDay: TimesheetEntry2[] = [];

		timesheet.entries.forEach((entry) => {
			entriesCurrentDay.push({
				name: entry.name,
				metadata: [entry.metadata[daysFromStart]],
				durations: [entry.durations[daysFromStart]],
			});

			totalTimePerDay += entry.durations[daysFromStart];
		});

		entriesCurrentDay.forEach((entry) => {
			const name = entry.name;
			const duration = entry.durations[0];

			if (duration !== 0) {
				if (percentRemaining > 0) {
					// calculate the percentage of bar that will be filled with the hours used and make sure that limit does not exceed 100%
					percentUsed = (duration / totalTimePerDay) * 100;
					const percentEndLimit = percentRemaining - percentUsed >= 0 ? percentStart + percentUsed : 100;
					const entryColor = HelperFunctions.getEntryTypeColor(entry);
					linearGradient += `, ${entryColor} ${percentStart}% ${percentEndLimit}%`;

					// reset the parameters for the next timesheet entry values in the linear gradient
					percentStart = percentStart + percentUsed;
					percentRemaining = percentRemaining - percentUsed;
				}
			}
		});

		return `background-repeat: no-repeat; background-image: linear-gradient(to right${linearGradient})`;
	}
	getEntryTypeColor = (entry: TimesheetEntry2) => HelperFunctions.getEntryTypeColor(entry);
	getTimesheetEntryColor = (entryName: string): TimesheetEntryColors => HelperFunctions.getTimesheetEntryColor(entryName);

	getTimesheetEntryColorStyling(entry: TimesheetEntry2) {
		const color = this.getEntryTypeColor(entry);
		const style = { 'background-color': color };
		return style;
	}

	getTimeEntryEntryTimeAmount(entry: TimesheetEntry2, daysFromStart: number) {
		const duration = entry.durations[daysFromStart];

		if (`${duration}`.length === 1) {
			return `${duration}.0 hrs`;
		}

		return `${duration} hrs`;
	}

	getTotalCalculatedTime(timesheet: Timesheet, daysFromStart: number) {
		let totalTime = 0;
		timesheet.entries.forEach((entry) => (totalTime += entry.durations[daysFromStart]));

		if (`${totalTime}`.length === 1) {
			return `${totalTime}.0 hrs`;
		}

		return `${totalTime} hrs`;
	}

	getIsSavedOrPredictedData(timesheet: Timesheet) {
		return timesheet.status === TimesheetStatus.Predicted || timesheet.status === TimesheetStatus.Saved;
	}

	isFilterEntry(timesheet: Timesheet, daysFromStart: number) {
		return (
			this.getIsCDODay(timesheet, daysFromStart) ||
			this.getIsStatHoliday(timesheet, daysFromStart) ||
			this.getIsPayDay(timesheet, daysFromStart) ||
			this.getIsHolidayClosure(timesheet, daysFromStart)
		);
	}

	getHasEntryWithDuration(entry: TimesheetEntry2, currentDay: number) {
		return entry.durations[currentDay] > 0;
	}
}
